import { ref, readonly } from 'vue'
import { checkMemberGroups } from '@/plugins/msal'

const VITE_AZG_ADMIN_GROUP_ID = import.meta.env.VITE_AZG_ADMIN_GROUP_ID
const VITE_AZG_TIMESHEET_ADMIN_GROUP_ID = import.meta.env.VITE_AZG_TIMESHEET_ADMIN_GROUP_ID
const VITE_AZG_ROLE_SI_MANAGEMENT_GROUP_ID = import.meta.env.VITE_AZG_ROLE_SI_MANAGEMENT_GROUP_ID
const VITE_AZG_ROLE_SI_DEV_ADMIN_GROUP_ID = import.meta.env.VITE_AZG_ROLE_SI_DEV_ADMIN_GROUP_ID
const VITE_AZG_PROJECT_INTAKE_GROUP_ID = import.meta.env.VITE_AZG_PROJECT_INTAKE_GROUP_ID
const VITE_AZG_APP_PEOPLE_TESTERS_GROUP_ID = import.meta.env.VITE_AZG_APP_PEOPLE_TESTERS_GROUP_ID
const VITE_AZG_COMPASS_GROUP_ID = import.meta.env.VITE_AZG_COMPASS_GROUP_ID

const userIsAdmin = ref(false)
const userIsTimesheetAdmin = ref(false)
const userIsLeaveAdmin = ref(false)
const userIsProjectIntake = ref(false)
const userIsTester = ref(false)
const userIsCompass = ref(false)

export interface UserRoles {
  isAdmin: boolean
  isTimesheetAdmin: boolean
  isProjectIntake: boolean
  isTester: boolean
  isCompass: boolean
}

export function useUserRoles() {
  const setRoles = (roles: UserRoles) => {
    userIsAdmin.value = roles.isAdmin
    userIsTimesheetAdmin.value = roles.isTimesheetAdmin
    // Leave-admin is (nog) niet gekoppeld aan een groep
    userIsLeaveAdmin.value = false
    userIsProjectIntake.value = roles.isProjectIntake
    userIsTester.value = roles.isTester
    userIsCompass.value = roles.isCompass
  }

  const clearRoles = () => {
    userIsAdmin.value = false
    userIsTimesheetAdmin.value = false
    userIsLeaveAdmin.value = false
    userIsProjectIntake.value = false
    userIsTester.value = false
    userIsCompass.value = false
  }

  /**
   * Bepaalt alle rollen met één Graph-call (/me/checkMemberGroups) en zet ze.
   * Enige plek waar groeps-ID's aan rollen worden gekoppeld (router-guard en App.vue).
   * Een niet-gezette env-var betekent: niemand heeft die rol.
   */
  const resolveAndSetRoles = async (): Promise<UserRoles> => {
    const memberOf = await checkMemberGroups([
      VITE_AZG_ADMIN_GROUP_ID,
      VITE_AZG_TIMESHEET_ADMIN_GROUP_ID,
      VITE_AZG_ROLE_SI_MANAGEMENT_GROUP_ID,
      VITE_AZG_ROLE_SI_DEV_ADMIN_GROUP_ID,
      VITE_AZG_PROJECT_INTAKE_GROUP_ID,
      VITE_AZG_APP_PEOPLE_TESTERS_GROUP_ID,
      VITE_AZG_COMPASS_GROUP_ID,
    ])
    // has() op een lege/ontbrekende ID is altijd false: checkMemberGroups filtert die weg
    const isMember = (groupId: string | undefined) => Boolean(groupId) && memberOf.has(groupId as string)

    const roles: UserRoles = {
      isAdmin: isMember(VITE_AZG_ADMIN_GROUP_ID),
      // Timesheet-admin = lid van een van de 3 groepen
      isTimesheetAdmin:
        isMember(VITE_AZG_TIMESHEET_ADMIN_GROUP_ID) ||
        isMember(VITE_AZG_ROLE_SI_MANAGEMENT_GROUP_ID) ||
        isMember(VITE_AZG_ROLE_SI_DEV_ADMIN_GROUP_ID),
      isProjectIntake: isMember(VITE_AZG_PROJECT_INTAKE_GROUP_ID),
      isTester: isMember(VITE_AZG_APP_PEOPLE_TESTERS_GROUP_ID),
      // Bewust geen admin-uitzondering: de Compass-API doet dezelfde groepscheck
      isCompass: isMember(VITE_AZG_COMPASS_GROUP_ID),
    }
    setRoles(roles)
    return roles
  }

  return {
    // Readonly refs zodat child components ze niet kunnen wijzigen
    userIsAdmin: readonly(userIsAdmin),
    userIsTimesheetAdmin: readonly(userIsTimesheetAdmin),
    userIsLeaveAdmin: readonly(userIsLeaveAdmin),
    userIsProjectIntake: readonly(userIsProjectIntake),
    userIsTester: readonly(userIsTester),
    userIsCompass: readonly(userIsCompass),
    // Alleen voor de router-guard en App.vue
    resolveAndSetRoles,
    clearRoles
  }
}
